import React, { useState, useEffect } from 'react';
import { Card, Steps, Button, Typography, Row, Col, Space, Badge, Tag, Input, Select, Alert, message, Spin, InputNumber } from 'antd';
import {
  CheckCircleOutlined,
  RocketOutlined,
  SendOutlined,
  ThunderboltOutlined,
  FireOutlined,
  LinkOutlined,
  CopyOutlined,
  EditOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useAuth } from '../hooks/useAuth';
import {
  useGetBrandsQuery,
  useCompleteOnboardingMutation,
  useCreateSurveyMutation,
  useGetSurveyCohortsQuery,
  useUpdateBrandNameMutation
} from '../services/nestjs-base-api';
import { TemplateTypeOptions, TemplateTypeOptions3P, TemplateTypeEnum } from '../enums/TemplateTypeEnum';
import { Utils } from '../utils/utils';
import '../styles/revolutionary-dashboard.css';

const { Title, Paragraph, Text } = Typography;
const { Step } = Steps;
const { TextArea } = Input;

interface OnboardingWizardProps {
  onComplete: () => void;
  forceStep?: number;
}

export const SimpleOnboardingWizard: React.FC<OnboardingWizardProps> = ({ onComplete, forceStep }) => {
  const intl = useIntl();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Get default welcome message from intl
  const defaultWelcomeMessage = intl.formatMessage({ id: 'onboarding.survey.welcomeMessageDefault' });

  // Get initial state ONCE and memoize it
  const initialState = React.useMemo(() => {
    const savedWizardState = sessionStorage.getItem('simpleOnboardingWizardState');
    const state = savedWizardState && forceStep === undefined
      ? JSON.parse(savedWizardState)
      : {
          currentStep: forceStep !== undefined ? forceStep : 0,
          brandNameConfirmed: forceStep !== undefined && forceStep > 0,
          surveyCreated: forceStep === 2,
          createdSurveyHash: forceStep === 2 ? 'demo-hash' : '',
          surveyName: forceStep === 2 ? 'Demo Survey' : '',
          surveyDescription: forceStep === 2 ? 'This is a demo survey for testing purposes' : defaultWelcomeMessage,
          businessModel: forceStep === 2 ? 'dtc' : '',
          productType: forceStep === 2 ? 'durable' : '',
          questionsPerParticipant: 10,
          createdSurveys: forceStep === 2 ? [] : [],
          brandName: ''
        };

    // If surveyDescription is empty/undefined, use default
    if (!state.surveyDescription || state.surveyDescription.trim() === '') {
      state.surveyDescription = defaultWelcomeMessage;
    }

    return state;
  }, [defaultWelcomeMessage]); // Updated deps

  const [currentStep, setCurrentStep] = useState(initialState.currentStep);
  const [completeOnboarding] = useCompleteOnboardingMutation();
  const [createSurvey] = useCreateSurveyMutation({
    fixedCacheKey: 'onboarding-survey-creation'
  });
  const [updateBrandName] = useUpdateBrandNameMutation();
  const [brandNameConfirmed, setBrandNameConfirmed] = useState(initialState.brandNameConfirmed);
  const [surveyCreated, setSurveyCreated] = useState(initialState.surveyCreated);
  const [createdSurveys, setCreatedSurveys] = useState<Array<{ hash: string; cohortId: number }>>(
    initialState.createdSurveys || []
  );
  const [isCreatingSurvey, setIsCreatingSurvey] = useState(false);
  const [isUpdatingBrandName, setIsUpdatingBrandName] = useState(false);

  // Form data
  const [brandName, setBrandName] = useState(initialState.brandName);
  const [surveyDescription, setSurveyDescription] = useState(initialState.surveyDescription);
  const [businessModel, setBusinessModel] = useState<string>(initialState.businessModel || '');
  const [productType, setProductType] = useState<string>(initialState.productType);
  const [questionsPerParticipant, setQuestionsPerParticipant] = useState(initialState.questionsPerParticipant);

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Save state to session storage whenever it changes
  useEffect(() => {
    const stateToSave = {
      currentStep,
      brandNameConfirmed,
      surveyCreated,
      createdSurveys,
      brandName,
      surveyDescription,
      businessModel,
      productType,
      questionsPerParticipant
    };
    sessionStorage.setItem('simpleOnboardingWizardState', JSON.stringify(stateToSave));
  }, [currentStep, brandNameConfirmed, surveyCreated, createdSurveys, brandName, surveyDescription, businessModel, productType, questionsPerParticipant]);

  // Fetch brands 
  const { data: brands = [], isLoading: brandsLoading } = useGetBrandsQuery({
    jwtToken: user?.jwtToken || '',
    organisationId: user?.organisation?.id || 0,
  }, {
    skip: !user?.jwtToken || !user?.organisation?.id,
  });

  // Fetch cohorts
  const { data: cohorts = [], isLoading: cohortsLoading } = useGetSurveyCohortsQuery({
    jwtToken: user?.jwtToken || '',
    organisationId: user?.organisation?.id || 0,
  }, {
    skip: !user?.jwtToken || !user?.organisation?.id,
  });

  // Get the first/primary brand
  const primaryBrand = brands[0];

  // Set brand name from primaryBrand when it's loaded
  useEffect(() => {
    if (primaryBrand && !brandName) {
      setBrandName(primaryBrand.name);
    }
  }, [primaryBrand, brandName]);

  // Get all active cohorts for creating multiple surveys
  const activeCohorts = cohorts.filter(c => c.isActive);

  // Auto-select Durable Goods for 3P Retailer
  useEffect(() => {
    if (businessModel === '3p_retailer' && productType !== TemplateTypeEnum.THREE_P_DURABLE) {
      setProductType(TemplateTypeEnum.THREE_P_DURABLE);
    }
  }, [businessModel, productType]);

  const handleConfirmBrandName = async () => {
    // Validate brand name
    if (!brandName || brandName.trim() === '') {
      setErrors({ brandName: intl.formatMessage({ id: 'onboarding.brandName.required' }) });
      return;
    }

    setIsUpdatingBrandName(true);
    setErrors({});

    try {
      // Only update if the brand name has changed
      if (primaryBrand && brandName !== primaryBrand.name) {
        await updateBrandName({
          jwtToken: user?.jwtToken!,
          organisationId: user?.organisation?.id!,
          brandId: primaryBrand.id,
          name: brandName.trim()
        }).unwrap();

        message.success(intl.formatMessage({ id: 'onboarding.brandName.updateSuccess' }));
      }

      // Mark brand name as confirmed and move to next step
      setBrandNameConfirmed(true);
      setCurrentStep(1);
      setIsUpdatingBrandName(false);
    } catch (error: any) {
      console.error('Failed to update brand name:', error);
      message.error(intl.formatMessage({ id: 'onboarding.brandName.updateError' }));
      setIsUpdatingBrandName(false);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!businessModel) {
      newErrors.businessModel = 'Business model is required';
    }

    if (!productType) {
      newErrors.productType = 'Product type is required';
    }

    if (!primaryBrand) {
      newErrors.general = 'Unable to create surveys: missing brand data';
    }

    if (!activeCohorts || activeCohorts.length === 0) {
      newErrors.general = 'Unable to create surveys: no active cohorts found';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateSurvey = async () => {
    // Clear previous errors
    setErrors({});

    // Validate inputs
    if (!validateForm()) {
      console.error('Form validation failed');
      return;
    }

    setIsCreatingSurvey(true);

    try {
      // Auto-generate survey name
      const surveyName = `${primaryBrand.name} Brand Survey`;

      // Create surveys for all active cohorts
      const createdSurveyData: Array<{ hash: string; cohortId: number }> = [];

      for (const cohort of activeCohorts) {
        const payload: any = {
          name: surveyName,
          brandId: primaryBrand.id,
          cohortId: cohort.id,
          description: surveyDescription || undefined,
          startsAt: undefined,
          endsAt: undefined,
          questionsPerResponse: questionsPerParticipant,
          rotationStrategy: 'balanced' as const,
          theme: {
            primaryColor: '#4338ca',
            secondaryColor: '#1e40af',
            backgroundColor: '#1e293b',
            cardColor: '#334155',
            accentColor: '#F59E0B'
          },
          showProgressBar: true,
          animationSpeed: 800,
          allowSkip: false,
          allowBackNavigation: true,
          persistProgress: true,
          expirationHours: 24,
          completionTitle: 'Survey Complete!',
          completionSubtitle: 'Thank you for taking the time to share your feedback...',
          completionButtonText: 'Visit our website',
          completionButtonLink: undefined,
          showCompletionButton: false,
          completionSecondaryButtonText: 'Leave us a review',
          completionSecondaryButtonLink: undefined,
          showCompletionSecondaryButton: false,
          collectEmail: true,
          emailRequired: true,
          emailPlaceholder: 'Enter your email address',
          npsRequired: true,
          showWelcomePage: true,
          productType: productType
        };

        const result = await createSurvey({
          jwtToken: user?.jwtToken!,
          organisationId: user?.organisation?.id!,
          payload
        }).unwrap();

        createdSurveyData.push({ hash: result.hash, cohortId: cohort.id });
      }

      // Save wizard state to sessionStorage IMMEDIATELY
      const newState = {
        currentStep: 2,
        brandNameConfirmed: true,
        surveyCreated: true,
        createdSurveys: createdSurveyData,
        brandName,
        surveyDescription,
        businessModel,
        productType,
        questionsPerParticipant
      };
      sessionStorage.setItem('simpleOnboardingWizardState', JSON.stringify(newState));

      // Update React state
      setCreatedSurveys(createdSurveyData);
      setSurveyCreated(true);
      setCurrentStep(2);
      setIsCreatingSurvey(false);

      message.success(`${createdSurveyData.length} surveys created successfully!`);
    } catch (error: any) {
      console.error('Failed to create surveys:', error);
      message.error('Failed to create surveys. Please try again or use the full survey creation page.');
      setIsCreatingSurvey(false);
    }
  };

  const handleFinalComplete = async () => {
    // Mark onboarding as completed in the backend
    if (user?.jwtToken) {
      try {
        await completeOnboarding({ jwtToken: user.jwtToken });
        // Clear the session storage
        sessionStorage.removeItem('simpleOnboardingWizardState');
        onComplete();
      } catch (error) {
        console.error('Failed to complete onboarding:', error);
      }
    }
  };

  const handleViewSurveyDetails = () => {
    // Navigate to surveys list page
    navigate('/dashboard/surveys');
  };

  // Helper function to get cohort display name
  const getCohortDisplayName = (cohortId: number): string => {
    const cohort = cohorts.find(c => c.id === cohortId);
    if (!cohort) return 'Unknown Cohort';

    // Show "All Customers" instead of "Random Customers"
    if (cohort.code === 'random_customers') {
      return 'All Customers';
    }

    return cohort.name;
  };

  const getCohortDescription = (cohortId: number): string => {
    const cohort = cohorts.find(c => c.id === cohortId);
    return cohort?.description || '';
  };

  const steps = [
    {
      title: intl.formatMessage({ id: 'onboarding.brandName.stepTitle' }),
      icon: <EditOutlined style={{ fontSize: '20px' }} />,
      completed: brandNameConfirmed,
      content: (
        <Card
          className="revolutionary-step-card glassmorphism-card"
          style={{
            background: 'linear-gradient(135deg, #fffefb 0%, #fffbeb 100%)',
            border: '2px solid rgba(245, 158, 11, 0.2)',
            borderRadius: '20px',
            overflow: 'hidden'
          }}
          styles={{ body: { padding: 0 } }}
        >
          <Row gutter={0} align="stretch" style={{ minHeight: '500px' }}>
            <Col xs={24} md={10} style={{
              background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(245, 158, 11, 0.05))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px'
            }}>
              <div style={{ textAlign: 'center' }}>
                <Badge dot style={{ background: '#F59E0B' }}>
                  <EditOutlined style={{ fontSize: '80px', color: '#F59E0B' }} />
                </Badge>
                <div style={{ marginTop: '32px', maxWidth: '300px' }}>
                  <Title level={4} style={{
                    fontSize: '22px',
                    color: '#F59E0B',
                    fontWeight: '700',
                    lineHeight: '1.4',
                    margin: 0,
                    marginBottom: '8px'
                  }}>
                    {intl.formatMessage({ id: 'onboarding.brandName.leftTitle' })}
                  </Title>
                  <Text style={{
                    fontSize: '16px',
                    color: '#6b7280',
                    fontWeight: '400',
                    lineHeight: '1.5',
                    display: 'block'
                  }}>
                    {intl.formatMessage({ id: 'onboarding.brandName.leftSubtitle' })}
                  </Text>
                </div>
              </div>
            </Col>
            <Col xs={24} md={14} style={{ padding: '40px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <CheckCircleOutlined style={{ color: '#F59E0B', fontSize: '24px' }} />
                <Title level={3} style={{
                  color: '#F59E0B',
                  margin: 0,
                  fontWeight: '700'
                }}>
                  {intl.formatMessage({ id: 'onboarding.brandName.mainTitle' })}
                </Title>
              </div>

              <Alert
                type="warning"
                message={intl.formatMessage({ id: 'onboarding.brandName.alertTitle' })}
                description={intl.formatMessage({ id: 'onboarding.brandName.alertDescription' })}
                showIcon
                style={{ marginBottom: '24px', background: '#fffbeb', border: '1px solid #fde68a' }}
              />

              {brandsLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Spin size="large" />
                  <div style={{ marginTop: '16px', color: '#6b7280' }}>
                    {intl.formatMessage({ id: 'onboarding.brandName.loading' })}
                  </div>
                </div>
              ) : !primaryBrand ? (
                <Alert
                  type="error"
                  message={intl.formatMessage({ id: 'onboarding.brandName.errorSetup' })}
                  description={intl.formatMessage({ id: 'onboarding.brandName.errorSetupDescription' })}
                  showIcon
                />
              ) : (
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
                      {intl.formatMessage({ id: 'onboarding.brandName.label' })} <span style={{ color: 'red' }}>*</span>
                    </label>
                    <Input
                      size="large"
                      value={brandName}
                      onChange={(e) => {
                        setBrandName(e.target.value);
                        if (errors.brandName) {
                          setErrors(prev => ({ ...prev, brandName: '' }));
                        }
                      }}
                      placeholder={intl.formatMessage({ id: 'onboarding.brandName.placeholder' })}
                      status={errors.brandName ? 'error' : ''}
                      style={{ marginBottom: errors.brandName ? '4px' : '4px' }}
                    />
                    {errors.brandName && (
                      <div style={{ color: '#ff4d4f', fontSize: '12px', marginBottom: '4px' }}>
                        {errors.brandName}
                      </div>
                    )}
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {intl.formatMessage({ id: 'onboarding.brandName.helpText' })}
                    </Text>
                  </div>

                  {/* Preview section */}
                  <Card
                    style={{
                      background: 'linear-gradient(135deg, #fffefb 0%, #fffbeb 100%)',
                      border: '2px solid rgba(245, 158, 11, 0.3)',
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(245, 158, 11, 0.1)'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '16px',
                      paddingBottom: '12px',
                      borderBottom: '1px solid rgba(245, 158, 11, 0.2)'
                    }}>
                      <div style={{
                        background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                        borderRadius: '8px',
                        padding: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <CheckCircleOutlined style={{ fontSize: '20px', color: 'white' }} />
                      </div>
                      <Text strong style={{ fontSize: '15px', color: '#F59E0B' }}>
                        {intl.formatMessage({ id: 'onboarding.brandName.previewTitle' })}
                      </Text>
                    </div>
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                      <Text type="secondary" style={{ fontSize: '13px', display: 'block', marginLeft: '8px' }}>
                        {intl.formatMessage({ id: 'onboarding.brandName.previewLabel' })}
                      </Text>
                      <div style={{
                        background: 'white',
                        borderRadius: '8px',
                        padding: '16px',
                        border: '1px solid rgba(245, 158, 11, 0.15)'
                      }}>
                        <div style={{ fontSize: '15px', fontWeight: '500', marginBottom: '12px', color: '#374151', lineHeight: '1.6' }}>
                          "{intl.formatMessage(
                            { id: 'onboarding.brandName.preview1' },
                            { brandName: <strong style={{ color: '#F59E0B', background: 'rgba(245, 158, 11, 0.1)', padding: '2px 6px', borderRadius: '4px' }}>{brandName || intl.formatMessage({ id: 'onboarding.brandName.previewPlaceholder' })}</strong> }
                          )}"
                        </div>
                        <div style={{ fontSize: '15px', fontWeight: '500', marginBottom: '12px', color: '#374151', lineHeight: '1.6' }}>
                          "{intl.formatMessage(
                            { id: 'onboarding.brandName.preview2' },
                            { brandName: <strong style={{ color: '#F59E0B', background: 'rgba(245, 158, 11, 0.1)', padding: '2px 6px', borderRadius: '4px' }}>{brandName || intl.formatMessage({ id: 'onboarding.brandName.previewPlaceholder' })}</strong> }
                          )}"
                        </div>
                        <div style={{ fontSize: '15px', fontWeight: '500', color: '#374151', lineHeight: '1.6' }}>
                          "{intl.formatMessage(
                            { id: 'onboarding.brandName.preview3' },
                            { brandName: <strong style={{ color: '#F59E0B', background: 'rgba(245, 158, 11, 0.1)', padding: '2px 6px', borderRadius: '4px' }}>{brandName || intl.formatMessage({ id: 'onboarding.brandName.previewPlaceholder' })}</strong> }
                          )}"
                        </div>
                      </div>
                    </Space>
                  </Card>

                  <Button
                    type="primary"
                    size="large"
                    icon={<CheckCircleOutlined style={{ marginRight: 8 }} />}
                    onClick={handleConfirmBrandName}
                    loading={isUpdatingBrandName}
                    style={{
                      background: 'linear-gradient(135deg, #F59E0B, #FBBF24)',
                      border: 'none',
                      height: '48px',
                      fontWeight: '600',
                      fontSize: '16px',
                      width: '100%',
                      color: 'white'
                    }}
                    className="revolutionary-button"
                  >
                    {brandName === primaryBrand?.name
                      ? intl.formatMessage({ id: 'onboarding.brandName.buttonConfirm' })
                      : intl.formatMessage({ id: 'onboarding.brandName.buttonUpdate' })
                    }
                  </Button>
                </Space>
              )}
            </Col>
          </Row>
        </Card>
      ),
    },
    {
      title: activeCohorts.length === 1 ? 'Create Your First Survey' : 'Create Your Cohort Surveys',
      icon: <RocketOutlined style={{ fontSize: '20px' }} />,
      completed: surveyCreated,
      content: (
        <Card 
          className="revolutionary-step-card glassmorphism-card"
          style={{
            background: 'linear-gradient(135deg, #fffefb 0%, #fffbeb 100%)',
            border: '2px solid rgba(245, 158, 11, 0.2)',
            borderRadius: '20px',
            overflow: 'hidden'
          }}
          styles={{ body: { padding: 0 } }}
        >
          <Row gutter={0} align="stretch" style={{ minHeight: '500px' }}>
            <Col xs={24} md={10} style={{ 
              background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(245, 158, 11, 0.05))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px'
            }}>
              <div style={{ textAlign: 'center' }}>
                <Badge dot style={{ background: '#52c41a' }}>
                  <RocketOutlined style={{ fontSize: '80px', color: '#F59E0B' }} />
                </Badge>
                <div style={{ marginTop: '32px', maxWidth: '300px' }}>
                  <Title level={4} style={{ 
                    fontSize: '22px', 
                    color: '#F59E0B', 
                    fontWeight: '700',
                    lineHeight: '1.4',
                    margin: 0,
                    marginBottom: '8px'
                  }}>
                    Let's get you set up quickly!
                  </Title>
                  <Text style={{ 
                    fontSize: '16px', 
                    color: '#6b7280', 
                    fontWeight: '400',
                    lineHeight: '1.5',
                    display: 'block'
                  }}>
                    Start measuring your brand equity and unlock powerful customer insights
                  </Text>
                </div>
              </div>
            </Col>
            <Col xs={24} md={14} style={{ padding: '40px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <FireOutlined style={{ color: '#F59E0B', fontSize: '24px' }} />
                <Title level={3} style={{ 
                  color: '#F59E0B',
                  margin: 0,
                  fontWeight: '700'
                }}>
                  Quick Survey Setup
                </Title>
              </div>
              
              {brandsLoading || cohortsLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Spin size="large" />
                  <div style={{ marginTop: '16px', color: '#6b7280' }}>Loading setup data...</div>
                </div>
              ) : !primaryBrand || activeCohorts.length === 0 ? (
                <Alert
                  type="error"
                  message="Setup Error"
                  description="Unable to find required brand or cohort data. Please contact support."
                  showIcon
                />
              ) : (
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  {activeCohorts.length === 1 ? (
                    <Alert
                      type="warning"
                      message={`Survey will be named: "${primaryBrand.name} Brand Survey"`}
                      showIcon
                      style={{ marginBottom: '8px', background: '#fffbeb', border: '1px solid #fde68a' }}
                    />
                  ) : (
                    <Alert
                      type="warning"
                      message="Multiple Customer Cohort Surveys"
                      description={
                        <div style={{ fontSize: '13px' }}>
                          <div style={{ marginBottom: '8px' }}>
                            We'll automatically create <strong>{activeCohorts.length} separate surveys</strong> for your different customer groups:
                          </div>
                          <ul style={{ marginBottom: '8px', paddingLeft: '20px' }}>
                            {activeCohorts.map((cohort, index) => (
                              <li key={index}>
                                <strong>{getCohortDisplayName(cohort.id)}:</strong> {cohort.description}
                              </li>
                            ))}
                          </ul>
                          <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '8px' }}>
                            Each cohort gets a unique survey link so you can compare how different customer segments perceive your brand. All surveys will be named: "{primaryBrand.name} Brand Survey"
                          </div>
                        </div>
                      }
                      showIcon
                      style={{ marginBottom: '8px', background: '#fffbeb', border: '1px solid #fde68a' }}
                    />
                  )}

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
                      {intl.formatMessage({ id: 'onboarding.survey.welcomeMessageLabel' })}
                    </label>
                    <TextArea
                      value={surveyDescription}
                      onChange={(e) => setSurveyDescription(e.target.value)}
                      rows={3}
                      maxLength={500}
                      showCount
                      style={{ marginBottom: '4px' }}
                    />
                    <div>
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                        {intl.formatMessage({ id: 'onboarding.survey.welcomeMessageHelp' })}
                      </Text>
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block', fontStyle: 'italic', marginTop: '2px', color: '#F59E0B' }}>
                        💡 {intl.formatMessage({ id: 'onboarding.survey.welcomeMessageRecommended' })}
                      </Text>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
                      Business Model <span style={{ color: 'red' }}>*</span>
                    </label>
                    <Select
                      value={businessModel}
                      onChange={(value) => {
                        setBusinessModel(value);
                        // Clear error when user selects an option
                        if (errors.businessModel) {
                          setErrors(prev => ({ ...prev, businessModel: '' }));
                        }
                        // Reset product type when business model changes
                        setProductType('');
                      }}
                      placeholder="What type of business are you?"
                      size="large"
                      style={{ width: '100%', marginBottom: errors.businessModel ? '4px' : '4px' }}
                      status={errors.businessModel ? 'error' : ''}
                      optionLabelProp="label"
                    >
                      <Select.Option value="dtc" label="Direct to Consumer">
                        <div>
                          <div style={{ fontWeight: '500' }}>Direct to Consumer</div>
                          <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '2px' }}>Selling your own branded products</div>
                        </div>
                      </Select.Option>
                      <Select.Option value="3p_retailer" label="3rd Party Retailer">
                        <div>
                          <div style={{ fontWeight: '500' }}>3rd Party Retailer</div>
                          <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '2px' }}>Mostly selling products made by other companies</div>
                        </div>
                      </Select.Option>
                    </Select>
                    {errors.businessModel && (
                      <div style={{ color: '#ff4d4f', fontSize: '12px', marginBottom: '4px' }}>
                        {errors.businessModel}
                      </div>
                    )}
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {businessModel === 'dtc'
                        ? 'You sell your own branded products directly to customers'
                        : businessModel === '3p_retailer'
                        ? 'You primarily sell products made by other companies'
                        : 'Select the model that best describes your business'}
                    </Text>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
                      Product Type <span style={{ color: 'red' }}>*</span>
                    </label>
                    <Select
                      value={productType}
                      onChange={(value) => {
                        setProductType(value);
                        // Clear error when user selects an option
                        if (errors.productType) {
                          setErrors(prev => ({ ...prev, productType: '' }));
                        }
                      }}
                      placeholder="What type of products do you sell?"
                      size="large"
                      disabled={!businessModel}
                      style={{ width: '100%', marginBottom: errors.productType ? '4px' : '4px' }}
                      options={businessModel === '3p_retailer' ? TemplateTypeOptions3P : TemplateTypeOptions}
                      status={errors.productType ? 'error' : ''}
                    />
                    {errors.productType && (
                      <div style={{ color: '#ff4d4f', fontSize: '12px', marginBottom: '4px' }}>
                        {errors.productType}
                      </div>
                    )}
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      This determines which question set will be used for your survey
                    </Text>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
                      Questions per Participant
                    </label>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      <InputNumber
                        value={questionsPerParticipant}
                        onChange={(value) => setQuestionsPerParticipant(value || 10)}
                        min={5}
                        max={15}
                        size="large"
                        style={{ width: '120px' }}
                      />
                      <div style={{ 
                        flex: 1,
                        background: 'rgba(245, 158, 11, 0.05)',
                        border: '1px solid rgba(245, 158, 11, 0.15)',
                        borderRadius: '8px',
                        padding: '12px',
                        fontSize: '13px',
                        color: '#6b7280'
                      }}>
                        <div style={{ marginBottom: '6px' }}>
                          <strong style={{ color: '#374151' }}>Recommended values:</strong>
                        </div>
                        <ul style={{ margin: 0, paddingLeft: '20px' }}>
                          <li><strong>5:</strong> Quick & easy, needs more responses</li>
                          <li><strong>10:</strong> Recommended balance</li>
                          <li><strong>15:</strong> More detailed, fewer responses needed</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {errors.general && (
                    <Alert
                      type="error"
                      message={errors.general}
                      showIcon
                      style={{ marginBottom: '16px' }}
                    />
                  )}

                  <Button
                    type="primary"
                    size="large"
                    icon={<ThunderboltOutlined />}
                    onClick={handleCreateSurvey}
                    loading={isCreatingSurvey}
                    style={{
                      background: 'linear-gradient(135deg, #F59E0B, #FBBF24)',
                      border: 'none',
                      height: '48px',
                      fontWeight: '600',
                      fontSize: '16px',
                      width: '100%',
                      color: 'white'
                    }}
                    className="revolutionary-button"
                  >
                    {activeCohorts.length === 1 ? 'Create Survey & Get Link' : `Create ${activeCohorts.length} Surveys & Get Links`}
                  </Button>
                </Space>
              )}
            </Col>
          </Row>
        </Card>
      ),
    },
    {
      title: activeCohorts.length === 1 ? 'Share Your Survey' : 'Share Your Surveys',
      icon: <SendOutlined style={{ fontSize: '20px' }} />,
      completed: false,
      content: (
        <Card 
          className="revolutionary-step-card glassmorphism-card"
          style={{
            background: 'linear-gradient(135deg, #fffefb 0%, #fffbeb 100%)',
            border: '2px solid rgba(245, 158, 11, 0.2)',
            borderRadius: '20px',
            overflow: 'hidden'
          }}
          styles={{ body: { padding: 0 } }}
        >
          <Row gutter={0} align="stretch" style={{ minHeight: '500px' }}>
            <Col xs={24} md={12} style={{ 
              background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(245, 158, 11, 0.05))',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              padding: '40px'
            }}>
              <div>
                <Title level={4} style={{
                  fontSize: '22px',
                  color: '#F59E0B',
                  fontWeight: '700',
                  marginBottom: '16px',
                  textAlign: 'center'
                }}>
                  {activeCohorts.length === 1
                    ? 'Share your survey link to start collecting insights'
                    : 'Share your survey links to start collecting insights'}
                </Title>

                {/* Display all survey links with cohort information */}
                {createdSurveys.length > 0 ? (
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    {createdSurveys.length > 1 && (
                      <Alert
                        type="success"
                        message="Multiple Cohort Surveys Created"
                        description="Each customer group has its own unique survey link below. Send each link to the corresponding customer segment to compare brand perception across different audiences."
                        showIcon
                        style={{ marginBottom: '8px' }}
                      />
                    )}
                    {[...createdSurveys].sort((a, b) => a.cohortId - b.cohortId).map((survey, index) => (
                      <Card
                        key={index}
                        style={{
                          background: index === 0 ? 'rgba(82, 196, 26, 0.05)' : 'rgba(245, 158, 11, 0.05)',
                          border: `1px solid ${index === 0 ? 'rgba(82, 196, 26, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`,
                          borderRadius: '12px',
                          padding: '20px 10px',
                        }}
                      >
                        <div style={{ marginBottom: '2px' }}>
                          <Text strong style={{ fontSize: '16px', color: '#F59E0B' }}>
                            {getCohortDisplayName(survey.cohortId)}
                          </Text>
                        </div>
                        <Text type="secondary" style={{ fontSize: '14px', display: 'block', marginBottom: '24px' }}>
                          {getCohortDescription(survey.cohortId)}
                        </Text>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '20px 24px', backgroundColor: 'white', borderRadius: 8 }}>
                          <LinkOutlined style={{ color: '#6b7280', fontSize: '14px' }} />
                          <Input
                            value={Utils.getPublicSurveyUrl(survey.hash)}
                            readOnly
                            style={{
                              flex: 1,
                              fontFamily: 'monospace',
                              fontSize: '12px',
                              border: 'none',
                              background: 'transparent',
                              padding: '0'
                            }}
                          />
                          <Button
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={() => {
                              const publicUrl = Utils.getPublicSurveyUrl(survey.hash);
                              navigator.clipboard.writeText(publicUrl).then(() => {
                                message.success(`${getCohortDisplayName(survey.cohortId)} survey link copied!`);
                              });
                            }}
                          />
                        </div>
                      </Card>
                    ))}
                  </Space>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                    <Spin />
                    <div style={{ marginTop: '16px' }}>Creating surveys...</div>
                  </div>
                )}
              </div>
            </Col>
            <Col xs={24} md={12} style={{ padding: '40px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              {/* Distribution Tips */}
              <div style={{ 
                background: 'rgba(82, 196, 26, 0.05)',
                border: '1px solid rgba(82, 196, 26, 0.2)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '24px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <SendOutlined style={{ color: '#52c41a' }} />
                  <Text strong>Share via:</Text>
                </div>
                <ul style={{ marginBottom: 0, paddingLeft: '20px', color: '#6b7280', fontSize: '13px' }}>
                  <li>Email campaigns to your customer list</li>
                  <li>Social media posts and stories</li>
                  <li>Website pop-ups or banners</li>
                  <li>QR codes in physical locations</li>
                  <li>SMS messages with the link</li>
                </ul>
              </div>

              {/* Pro Tips for Email Platforms */}
              <Alert
                type="warning"
                showIcon
                message="Pro Tip: Personalize Links in Email Platforms"
                description={
                  <div style={{ fontSize: '13px' }}>
                    <div style={{ marginBottom: '8px' }}>
                      Replace <Text code>{`{customer_identifier}`}</Text> in the survey link with your platform's email merge tag:
                    </div>
                    <ul style={{ marginBottom: 0, paddingLeft: '20px' }}>
                      <li><strong>Klaviyo:</strong> Replace with <Text code>{`{{ email }}`}</Text></li>
                      <li><strong>Mailchimp:</strong> Replace with <Text code>*|EMAIL|*</Text></li>
                      <li><strong>HubSpot:</strong> Replace with <Text code>{`{{ contact.email }}`}</Text></li>
                    </ul>
                    <div style={{ marginTop: '8px', color: '#8c8c8c' }}>
                      This allows you to track which customer submitted each response.
                    </div>
                  </div>
                }
                style={{ marginBottom: '16px', background: '#fffbeb', border: '1px solid #fde68a' }}
              />

              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Button
                  type="default"
                  size="large"
                  icon={<EditOutlined />}
                  onClick={handleViewSurveyDetails}
                  style={{
                    height: '48px',
                    fontWeight: '600',
                    fontSize: '16px',
                    width: '100%',
                    border: '2px solid #F59E0B'
                  }}
                >
                  View All Surveys
                </Button>

                <Button 
                  type="primary" 
                  size="large"
                  icon={<CheckCircleOutlined />}
                  onClick={handleFinalComplete}
                  style={{
                    background: 'linear-gradient(135deg, #F59E0B, #FBBF24)',
                    border: 'none',
                    height: '48px',
                    fontWeight: '600',
                    fontSize: '16px',
                    width: '100%',
                    color: 'white'
                  }}
                  className="revolutionary-button"
                >
                  Complete Setup & Go to Dashboard
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>
      ),
    },
  ];

  return (
    <div className="revolutionary-dashboard gpu-accelerated" style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Compact Revolutionary Hero Section */}
      <div style={{ 
        textAlign: 'center', 
        marginBottom: '32px',
        padding: '24px',
        background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 50%, #B45309 100%)',
        borderRadius: '16px',
        position: 'relative',
        overflow: 'hidden'
      }}
      className="revolutionary-hero glassmorphism-card"
      >
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(255,255,255,0.05)',
          backdropFilter: 'blur(10px)'
        }} />
        
        <div style={{ position: 'relative', zIndex: 2 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginBottom: '12px' }}>
            <FireOutlined style={{ fontSize: '32px', color: '#fff' }} />
            <Title level={2} style={{ 
              color: 'white',
              margin: 0,
              fontSize: '32px',
              fontWeight: '800',
              textShadow: '0 2px 8px rgba(0,0,0,0.2)'
            }}>
              Quick Setup Wizard
            </Title>
          </div>
          
          <Paragraph style={{
            fontSize: '16px',
            color: 'rgba(255,255,255,0.9)',
            maxWidth: '700px',
            margin: '0 auto 16px',
            fontWeight: '500'
          }}>
            Get your brand equity tracking up and running in just 3 simple steps
          </Paragraph>
          
          <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
            <Tag color="#52c41a" style={{ fontSize: '12px', fontWeight: '600' }}>
              Simplified Setup
            </Tag>
            <Tag color="#F59E0B" style={{ fontSize: '12px', fontWeight: '600' }}>
              Smart Defaults
            </Tag>
            <Tag color="#fa8c16" style={{ fontSize: '12px', fontWeight: '600' }}>
              Instant Launch
            </Tag>
          </div>
        </div>
      </div>

      {/* Progress Steps with Enhanced Styling */}
      <Card
        className="glassmorphism-card"
        style={{
          marginBottom: '32px',
          background: 'rgba(255,255,255,0.9)',
          border: '1px solid rgba(245, 158, 11, 0.2)',
          borderRadius: '16px'
        }}
      >
        <Steps 
          current={currentStep} 
          size="default"
          style={{ padding: '16px 0' }}
        >
          {steps.map((step, index) => (
            <Step
              key={index}
              title={
                <span style={{ 
                  fontWeight: '600',
                  fontSize: '16px',
                  color: step.completed ? '#52c41a' : index === currentStep ? '#F59E0B' : '#8c8c8c'
                }}>
                  {step.title}
                </span>
              }
              icon={
                step.completed ? (
                  <div style={{
                    background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <CheckCircleOutlined style={{ color: 'white', fontSize: '18px' }} />
                  </div>
                ) : (
                  <div style={{
                    background: index === currentStep 
                      ? 'linear-gradient(135deg, #F59E0B, #FBBF24)'
                      : '#f0f0f0',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {React.cloneElement(step.icon, { 
                      style: { 
                        ...step.icon.props.style,
                        color: index === currentStep ? 'white' : '#8c8c8c' 
                      }
                    })}
                  </div>
                )
              }
              status={step.completed ? 'finish' : index === currentStep ? 'process' : 'wait'}
            />
          ))}
        </Steps>
      </Card>

      {/* Step Content */}
      <div key={currentStep} style={{ minHeight: '400px' }}>
        {steps[currentStep]?.content}
      </div>

      <style>{`
        .revolutionary-step-card {
          border: none !important;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1) !important;
        }
        .revolutionary-step-card:hover {
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15) !important;
        }
        .revolutionary-step-card .ant-card-body {
          padding: 0 !important;
        }
        .ant-steps-item-finish .ant-steps-item-icon {
          background: transparent !important;
          border: none !important;
        }
        .ant-steps-item-process .ant-steps-item-icon {
          background: transparent !important;
          border: none !important;
        }
        .ant-steps-item-wait .ant-steps-item-icon {
          background: transparent !important;
          border: none !important;
        }
      `}</style>
    </div>
  );
};